import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK } from '@/game/config';
import { ProgressBar } from '@/game/ui/ProgressBar';
import { starGlyphsFor } from '@/game/starGlyphs';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { preRoundStats, type SessionSummary } from '@/game/progression/ReadingProgression';
import { getActivePack, packKey } from '@/game/packs';
import { recordStars } from '@/game/promo';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeShowClipOutro } from '@/game/ui/clipOutro';
import { maybeAskForRating } from '@/game/rating';
import { milestoneReached } from '@/game/milestones';
import { summaryLines, isFirstGame, type SummaryLine } from '@/game/summaryLines';
import { StreakChip, reducedMotion } from '@/game/ui/streakChip';
import { currentStreakView, streakEnabled, takeStreakEvent } from '@/game/progression/streakSession';

/** `stackStars`: the in-game planet column's star count for this game (RoundScene.sessionStars). */
interface SummaryInit { score: number; summary?: SessionSummary | null; stackStars?: number }

export class SummaryScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Summary' });
  }

  create(data: SummaryInit): void {
    this.input.setDefaultCursor('default');
    const summary = data.summary ?? null;
    let firstFlight = false;   // the first-game planet line is the record, so the gold one steps aside
    const topY = summary ? GAME_HEIGHT / 2 - 220 : GAME_HEIGHT / 2 - 60;

    this.add.text(GAME_WIDTH / 2, topY, i18next.t('summary.title'), {
      fontFamily: FONT_STACK, fontSize: '48px', color: '#ffffff',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, topY + 60, `${i18next.t('hud.score')}: ${data.score ?? 0}`, {
      fontFamily: FONT_STACK, fontSize: '32px', color: '#ffd166',
    }).setOrigin(0.5);

    // Reading-journey celebration: newly mastered words, stars earned, and the rocket nudge.
    if (summary) {
      // Once the journey is complete the wording has to change, or every further round claims
      // progress that can no longer happen. There are no NEW words left to master — a word only
      // re-enters the list after a slip knocks it out of the top box — and the rocket has nowhere to
      // move, so "keep going, you're getting there" is both wrong and deflating.
      const picked = summaryLines(summary, data.stackStars ?? 0);
      firstFlight = picked.some((l) => l.kind === 'firstFlight');
      const lines = picked.map((l) => this.lineText(l));

      this.add.text(GAME_WIDTH / 2, topY + 110, lines.join('\n'), {
        fontFamily: FONT_STACK, fontSize: '22px', color: '#4ecdc4', align: 'center',
        wordWrap: { width: GAME_WIDTH - 200 },
      }).setOrigin(0.5, 0);

      this.addRecords(summary, topY + 320, firstFlight);

      // Use the active lesson's reward art/caption (Unicorn Star for the fables journey, ABC Star
      // by default) so the end-of-round bar matches the menu and celebration screens.
      const reward = getActivePack().reward;
      const bar = new ProgressBar(this, GAME_WIDTH / 2, topY + 230, {
        width: 980,
        rewardIcon: reward?.icon,
        rewardLabelKey: reward?.labelKey,
        glyphs: starGlyphsFor(getActivePack()),
      });
      // Render the pre-round position, then rain a star per newly mastered word onto the bar so
      // the journey feels earned — each star landing nudges the rocket toward the ABC Planet.
      const start = preRoundStats(summary);
      bar.setStats(start, false);
      this.time.delayedCall(400, () => bar.animateGains(start, summary.stats));
      recordStars(summary.stats.stars, packKey(getActivePack()));
    }

    // End-of-game asks. Mutually exclusive by platform: the buy card is web-only (the browser
    // build is free and has to point somewhere), the rating sheet is paid-apps-only, and the
    // end-of-trial card is App-Clip-only (and only once that lesson's boards are spent).
    // All three wait for the star animation above to land, and none blocks the buttons below.
    maybeShowBuyPrompt(this);
    maybeShowClipOutro(this, { onPickLesson: () => this.scene.start('Menu', { pickLesson: true }) });
    maybeAskForRating(this, { kind: 'round', score: data.score ?? 0 });

    // Two real, side-by-side buttons with generous hit areas — replaces the old pair of small
    // stacked text links that sat 50px apart and invited miss-clicks. "Menu" is the safe default:
    // a full-screen catcher behind the buttons routes ANY click outside the Replay button back to
    // the menu, so a stray tap never re-runs the round and "how do I get out" is always answered.
    const btnY = GAME_HEIGHT / 2 + 240;
    const GAP = 24;

    // Full-screen catcher. With Phaser's default topOnly input, a click on the Replay button hits
    // only that button; clicks anywhere else fall through to this zone → Menu. Enabled after a short
    // delay so the tap that ended the round (or a lingering press) can't instantly bounce to menu.
    const catcher = this.add.zone(0, 0, GAME_WIDTH, GAME_HEIGHT).setOrigin(0).setDepth(0);
    catcher.on('pointerdown', () => this.scene.start('Menu'));
    this.time.delayedCall(300, () => catcher.setInteractive({ useHandCursor: true }));

    // "Play again" is the primary action (specs/first-game-summary.md §5): bigger, a ▶ so the shape
    // says "go" for a child who cannot read the label, and size, so it is told apart from the
    // grey button by more than colour. "Menu" stays reachable and labelled, just quieter. The catcher
    // above is deliberately unchanged: a stray tap still goes to the Menu, never into a new game.
    const menu = this.add.text(0, btnY, i18next.t('nav.menu'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee',
      padding: { x: 28, y: 14 },
    }).setOrigin(0.5).setDepth(10);
    menu.setInteractive({ useHandCursor: true });
    // The Menu button is interactive (hand cursor + pill), so with topOnly input it consumes its
    // own clicks instead of falling through to the catcher — it needs its own handler.
    menu.on('pointerdown', () => this.scene.start('Menu'));
    const retry = this.add.text(0, btnY, `▶  ${i18next.t('summary.retry')}`, {
      fontFamily: FONT_STACK, fontSize: '40px', color: '#ffffff',
      padding: { x: 54, y: 26 },
    }).setOrigin(0.5).setDepth(10);
    retry.setInteractive({ useHandCursor: true });
    retry.on('pointerdown', () => this.scene.start('Round'));

    // Center the pair: Menu on the left, Replay on the right. Position first, THEN draw the pill
    // backgrounds (addRoundedLabelBg snapshots the label's position when called).
    const totalW = menu.width + GAP + retry.width;
    menu.x = GAME_WIDTH / 2 - totalW / 2 + menu.width / 2;
    retry.x = GAME_WIDTH / 2 + totalW / 2 - retry.width / 2;
    addRoundedLabelBg(this, menu, { color: 0x3a4048, radius: 14 }).setDepth(9);
    const retryBg = addRoundedLabelBg(this, retry, { color: 0x2f80e0, radius: 20 });
    retryBg.setDepth(9);

    // After a lesson's first game the second one is the whole point of the screen, so the button
    // breathes — once the star animation has landed, and only until the child moves or 6 s pass.
    if (summary && isFirstGame(summary) && !reducedMotion()) {
      const pulse = this.tweens.add({
        targets: [retry, retryBg], scale: { from: 1, to: 1.05 }, duration: 900, yoyo: true,
        repeat: -1, ease: 'Sine.easeInOut', delay: 1200,
      });
      const stop = (): void => { pulse.stop(); retry.setScale(1); retryBg.setScale(1); };
      this.time.delayedCall(7200, stop);
      this.input.once('pointermove', stop);
    }

    // The day streak (specs/day-streak.md): the chip sits where it does on the menu, and whichever
    // screen shows first after a qualifying smash plays the celebration.
    if (streakEnabled()) {
      const chip = new StreakChip(this, currentStreakView(), { interactive: false });
      const ev = takeStreakEvent();
      if (ev) this.time.delayedCall(900, () => chip.playEvent(ev));
    }

    this.input.keyboard?.on('keydown-ESC', () => this.scene.start('Menu'));
  }

  /** A chosen line as the string the teal block shows (first-game-summary.md §4.1). */
  private lineText(l: SummaryLine): string {
    switch (l.kind) {
      case 'stars':
        // The string carries a literal ★; a lesson with its own star glyph (Halloween's spiders) swaps it.
        return i18next.t('summary.starsEarned', { count: l.count })
          .replace('★', starGlyphsFor(getActivePack()).full);
      case 'newlyMastered': {
        const shown = l.words.slice(0, 8).join(', ');
        const extra = l.words.length - 8;
        const key = l.done ? 'summary.refreshed' : 'summary.newlyMastered';
        return `${i18next.t(key, { count: l.count })}: ${shown}${extra > 0 ? ` +${extra}` : ''}`;
      }
      case 'upkeep':
        // Live numbers rather than a claim: a mastered word that was missed drops out of the top box.
        return i18next.t('summary.upkeep', { n: l.mastered, total: l.total });
      case 'firstFlight':
        return i18next.t('summary.firstFlight', { planet: i18next.t(`scoreboard.planets.${l.planet}`) });
      case 'almost':
        return i18next.t('summary.almostStars', { count: l.count });
      default:
        return i18next.t('summary.keepGoing');
    }
  }

  /**
   * The two records a game can break, in gold, under the journey bar: a faster board than this
   * lesson has ever seen, and a run that reached a planet no run reached before.
   *
   * This is the half of the record card that belongs on a game-over screen — not "here is
   * everything you have ever done", which is a page to read, but "here is the thing that changed in
   * the last three minutes", which is a reason to press Nochmal spielen. The card itself is one
   * screen away, on the menu (specs/scoreboard.md §5.2).
   *
   * Silent in the ordinary case: most games break nothing, and a line that appears every time is a
   * line nobody reads. It arrives a beat late and pops in, because a reward that was always there
   * is furniture.
   */
  private addRecords(summary: SessionSummary, y: number, planetAlreadyShown: boolean): void {
    const lines: string[] = [];
    if (summary.recordRoundMs !== null) {
      lines.push(i18next.t('summary.recordTime', { n: (summary.recordRoundMs / 1000).toFixed(1) }));
    }
    // Guarded rather than assumed: `recordStackStars` is only ever set alongside a named body, but
    // a lesson's first star has no planet and a missing name would print "undefined" in gold.
    const planet = summary.recordStackStars !== null && !planetAlreadyShown
      ? milestoneReached(summary.recordStackStars) : null;
    if (planet) {
      lines.push(i18next.t('summary.recordPlanet', {
        planet: i18next.t(`scoreboard.planets.${planet}`),
      }));
    }
    if (lines.length === 0) return;

    const text = this.add.text(GAME_WIDTH / 2, y, lines.join('\n'), {
      fontFamily: FONT_STACK, fontSize: '26px', color: '#ffd166', align: 'center',
      wordWrap: { width: GAME_WIDTH - 200 },
    }).setOrigin(0.5, 0).setAlpha(0).setScale(0.7);
    this.tweens.add({
      targets: text, alpha: 1, scale: 1, delay: 600, duration: 420, ease: 'Back.easeOut',
    });
  }
}
